import { defineStore } from 'pinia';
import { FormContext } from 'vee-validate';

import {
  PageVoid,
  WalletUserPrepaidInfo,
  WalletUserRepository,
} from '@/repositories/generated';
import {
  BalanceTypeKey,
  BalanceTypeKeys,
  ScheduleBalanceTypeKey,
  WalletUserBalanceDetailScheduleType,
  WalletUserBalanceDetailType,
  balanceUnit,
} from '@/types';
import {
  balanceAdjustScheduleTypeSchema,
  balanceAdjustSchema,
  useForm,
} from '@/validations';

type WalletUserBalanceDetailParam = {
  walletUserId: string;
  paymentMethodId: number;
  walletServiceId: string;
};

type BalanceDetailUiType = {
  valueTotal: number;
  pointTotal: number;
  typeDetail: Record<BalanceTypeKey, WalletUserBalanceDetailType>;
  scheduleTypeDetail: Record<ScheduleBalanceTypeKey, WalletUserBalanceDetailScheduleType>;
};

type BalanceDetailCtx = {
  param: WalletUserBalanceDetailParam | undefined;
  data: BalanceDetailUiType | undefined;
  lastUpdatedAt: string | undefined;
  loading: boolean;
  ignoreExpiredDate: boolean;
  walletService: {
    pointRatio: number;
  };
};

type UiForm = {
  differenceBalance: number | undefined;
  expiredDate: Date | undefined;
  isDeposit: boolean;
  reason: string | undefined;
  ignoreExpiredDate: boolean;
};

const EmptyError: Record<keyof UiForm, string> = {
  differenceBalance: '',
  expiredDate: '',
  isDeposit: '',
  reason: '',
  ignoreExpiredDate: '',
};

type State = {
  form: Record<BalanceTypeKey, FormContext<UiForm, UiForm>>;
  initData: BalanceDetailCtx;
};

const initialForm = () => useForm<UiForm>({
  validationSchema: balanceAdjustSchema,
  initialValues: {
    differenceBalance: undefined,
    isDeposit: true,
    reason: undefined,
  },
});

const initialScheduleTypeForm = () => useForm<UiForm>({
  validationSchema: balanceAdjustScheduleTypeSchema,
  initialValues: {
    differenceBalance: undefined,
    expiredDate: undefined,
    isDeposit: true,
    reason: undefined,
    ignoreExpiredDate: false,
  },
});

export const useBalanceAdjustStore = defineStore('balanceAdjust', {
  state: (): State => ({
    form: {
      PREPAID_VALUE: initialForm(),
      FUNDS_TRANSFER_VALUE: initialForm(),
      BONUS_VALUE: initialScheduleTypeForm(),
      BASIC_POINT: initialScheduleTypeForm(),
      BONUS_POINT: initialScheduleTypeForm(),
    },
    initData: {
      param: undefined,
      data: undefined,
      lastUpdatedAt: undefined,
      loading: false,
      ignoreExpiredDate: false,
      walletService: {
        pointRatio: 0,
      },
    },
  }),
  actions: {
    /**
     * initializer
     */
    initializeForm(type: BalanceTypeKey) {
      if (type === 'BONUS_VALUE') {
        this.form[type].setValues({ ignoreExpiredDate: this.initData.ignoreExpiredDate }, false);
      }
      this.form[type].setErrors(EmptyError);
    },
    async initializeData(
      walletUserId: string,
      prepaidInfo: WalletUserPrepaidInfo,
      pointRatio: number,
    ) {
      this.$reset();
      this.initData.ignoreExpiredDate = prepaidInfo.expiredDateUpdateAvailableFlg;

      const param = {
        walletUserId,
        paymentMethodId: prepaidInfo.prepaidPaymentMethodId,
        walletServiceId: prepaidInfo.walletServiceId,
      };
      this.initData.param = param;
      this.initData.loading = true;

      try {
        const balanceDetail = await WalletUserRepository.balance({
          walletUserId: param.walletUserId,
          paymentMethodId: param.paymentMethodId.toString(),
          walletServiceId: param.walletServiceId,
        });

        this.initData.data = {
          valueTotal: balanceDetail.valueBalance.valueTotal,
          pointTotal: balanceDetail.pointBalance.pointTotal,
          typeDetail: {
            PREPAID_VALUE: balanceDetail.valueBalance.prepaidValue,
            FUNDS_TRANSFER_VALUE: balanceDetail.valueBalance.fundsTransferValue,
            BONUS_VALUE: balanceDetail.valueBalance.bonusValue,
            BASIC_POINT: balanceDetail.pointBalance.basicPoint,
            BONUS_POINT: balanceDetail.pointBalance.bonusPoint,
          },
          scheduleTypeDetail: {
            BONUS_VALUE: balanceDetail.scheduleBalance.bonusValue,
            BASIC_POINT: balanceDetail.scheduleBalance.basicPoint,
            BONUS_POINT: balanceDetail.scheduleBalance.bonusPoint,
          },
        };
        this.initData.lastUpdatedAt = balanceDetail.lastUpdatedAt;
        this.initData.walletService = {
          pointRatio,
        };
      } catch {
        throw Error('残高情報の取得に失敗しました。');
      } finally {
        this.initData.loading = false;
      }
    },
    /**
     * validation, request
     */
    async validateForm(type: BalanceTypeKey): Promise<boolean> {
      const form = this.form[type];
      await form.validate();
      // Yupでチェックできない項目をここでチェックする
      if (!form.errors.differenceBalance && !form.values.isDeposit && this.getAdjustedBalance(type) < 0) {
        form.setFieldError('differenceBalance', '残高は0未満に減算できません。');
      }
      if (!form.errors.differenceBalance && !form.values.isDeposit && this.getAdjustedTotalBalance(type) < 0) {
        form.setFieldError('differenceBalance', '合計残高は0未満に減算できません。');
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isEmpty = (it: any) => Object.keys(it).length === 0;
      return isEmpty(form.errors);
    },
    async adjustBalance(type: BalanceTypeKey): Promise<PageVoid> {
      const {
        param,
        lastUpdatedAt,
      } = this.initData;

      if (param == null || lastUpdatedAt == null || this.form == null) {
        return Promise.reject();
      }
      const form = this.form[type].values;

      return WalletUserRepository.index1({
        walletUserId: param.walletUserId,
        requestBody: {
          adjustedBalance: this.getAdjustedBalance(type),
          balanceType: type,
          expiredDate: form.expiredDate?.toISOString(),
          isDeposit: form.isDeposit,
          lastUpdatedAt,
          paymentMethodId: param.paymentMethodId,
          reason: form.reason ?? '',
        },
      });
    },
    // 調整後残高
    getAdjustedBalance(type: BalanceTypeKey): number {
      if (this.initData.data == null) {
        return 0;
      }
      const { balanceTotal } = this.initData.data.typeDetail[type];
      const form = this.form[type].values;
      const diff = form.differenceBalance ?? 0;
      return form.isDeposit ? balanceTotal + diff : balanceTotal - diff;
    },
    // 調整後プリペイド合計残高 (ポイント比率を考慮)
    getAdjustedTotalBalance(type: BalanceTypeKey): number {
      if (this.initData.data == null) {
        return 0;
      }
      const multiplePointRatio = (balanceType: BalanceTypeKey, value: number) => (
        balanceUnit[balanceType] === 'pt'
          ? value * this.initData.walletService.pointRatio
          : value
      );
      const total = BalanceTypeKeys
        .map((it) => multiplePointRatio(it, this.initData.data?.typeDetail[it].balanceTotal ?? 0))
        .reduce((a, b) => a + b);

      const form = this.form[type].values;
      const diff = multiplePointRatio(type, form.differenceBalance ?? 0);
      return form.isDeposit ? total + diff : total - diff;
    },
  },
});
